<template>
  <div class="multi-user-selector-container">
    <user-short
      :id="'user-short-' + uuid"
      tabindex="-1"
      :user-id="entity.ownerId"
      :length="userShortSize"
      class="edit-user-short"
      mode="edit-view"
    >
    </user-short>
    <b-popover
      :target="'user-short-' + uuid"
      triggers="click blur"
      placement="bottom"
      custom-class="multi-user-popover"
    >
      <div class="form-group">
        <div class="selector-button-label text-medium">Owned by</div>
        <single-select
          :options="users"
          :value="owner"
          @select="setOwner($event)"
          placeholder="Choose an owner"
        >
          <template v-slot:default="item">
            <div class="d-flex user-pill">
              <user-short class="pl-2" :user-id="item.value.id" length="18"></user-short>
              <span class="user-text text-truncate" :title="item.value.displayName" v-no-capture>{{
                item.value.displayName
              }}</span>
            </div>
          </template>
          <template v-slot:listItem="option">
            <div class="d-flex dropdown-item-container">
              <user-short class="pl-2" :user-id="option.option.id" length="18"></user-short>
              <span class="pl-2 user-text" v-no-capture>{{ option.option.displayName }}</span>
            </div>
          </template>
        </single-select>
      </div>
      <div class="form-group">
        <div class="selector-button-label text-medium">Co-Owned by</div>
        <multi-select
          :options="selectableUsersList"
          :values="selectedCoOwners"
          @change="onCoOwnersChange($event)"
          placeholder="Choose co-owners"
        >
          <template v-slot:default="item">
            <div class="d-flex user-pill">
              <user-short class="pl-2" :user-id="item.value.id" length="18"></user-short>
              <span class="user-text text-truncate" :title="item.value.displayName">{{
                item.value.displayName
              }}</span>
            </div>
          </template>
          <template v-slot:listItem="option">
            <div class="d-flex dropdown-item-container">
              <user-short class="pl-2" :user-id="option.option.id" length="18"></user-short>
              <span class="pl-2 user-text">{{ option.option.displayName }}</span>
            </div>
          </template>
        </multi-select>
      </div>
    </b-popover>
  </div>
</template>

<script>
import { BPopover } from 'bootstrap-vue'
import * as R from 'ramda'
import UserShort from './UserShort'
import MultiSelect from '@/components/form/MultiSelect'
import SingleSelect from '@/components/form/SingleSelect'

let uuid = 0

export default {
  name: 'MultiUserSelector',
  components: {
    'user-short': UserShort,
    'b-popover': BPopover,
    MultiSelect,
    SingleSelect
  },
  props: {
    userIds: {},
    entity: {},
    userShortSize: { default: 22 }
  },
  beforeCreate() {
    this.uuid = 'multi-user-selector-' + uuid.toString()
    ++uuid
  },
  computed: {
    users() {
      return this.userIds
        .map(userId => this.$store.getters.userById(userId))
        .filter(user => !!user)
        .sort((a, b) => {
          return a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
        })
        .map(user => ({ ...user, label: user.displayName }))
    },
    owner() {
      if (this.entity.ownerId) {
        return this.$store.getters.userById(this.entity.ownerId)
      } else {
        return null
      }
    },
    selectedCoOwners() {
      return this.entity.coOwnerIds
        .map(userId => this.$store.getters.userById(userId))
        .filter(user => !!user)
    },
    selectableUsersList() {
      return R.differenceWith((x, y) => x.id === y.id, this.users, this.selectedCoOwners)
    }
  },
  methods: {
    updateUsers() {
      this.userIds.forEach(userId => {
        this.$store.dispatch('getUser', { id: userId })
      })
    },
    setOwner(user) {
      let entity = this.entity
      if (user) {
        entity.ownerId = user.id
        if (entity.coOwnerIds.indexOf(user.id) > -1) {
          entity.coOwnerIds = entity.coOwnerIds.filter(coOwnerId => coOwnerId !== user.id)
        }
      } else {
        entity.ownerId = null
      }
    },
    isCoOwner(user) {
      return this.entity.coOwnerIds.indexOf(user.id) > -1
    },
    onCoOwnersChange(values) {
      this.entity.coOwnerIds = values.map(R.prop('id'))
      if (this.entity.coOwnerIds.indexOf(this.entity.ownerId) > -1) {
        this.entity.ownerId = null
      }
    }
  },
  mounted() {
    this.updateUsers()
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom';

/deep/ .user-short-container:focus {
  outline: none;
}

.none-item-text {
  padding-left: 24px;
}

.multi-user-popover {
  width: $popover-max-width;
  border-radius: 16px;
}

/deep/ .none-item .dropdown-item {
  padding-left: 20px;
}

.form-group {
  margin-bottom: 12px;
}

.selector-button {
  border-radius: 20px;
  border: 1px solid #c4c4c4;
  font-size: 14px;
  padding: 7.5px 12px;
  line-height: 1;
}

.selector-button-label {
  padding-left: 14px;
  color: #8a8a8a;
  padding-bottom: 2px;
}

.selector-button-no-owner {
  color: #c4c4c4;
  padding: 2px 0;
}

.user-text {
  top: 2px;
  font-size: 0.875rem;
  line-height: 18px;
}

/deep/ .dropdown-item {
  padding-top: 0;
  padding-bottom: 0;
}

/deep/ .dropdown-menu {
  max-height: 250px;
  overflow-y: auto;
}

.dropdown-item-container {
  padding-top: 4px;
  padding-bottom: 4px;
  font-size: 0.875rem;
}

.edit-user-short {
  position: relative;
  cursor: pointer;

  .action-title {
    font-size: 0.5625rem;
    color: #c4c4c4;
    position: absolute;
    top: 22px;
    left: 50%;
    transform: translateX(-50%);
  }
}
.user-pill {
  padding: 2px 0 1px;

  /deep/ .user-short-container {
    padding: 0 5px !important;
  }
}
/deep/ .user-short-container {
  padding-top: 0 !important;
}
</style>
